import { box, fragment, image, link, row, svgCircle, svgClipPath, svgDefs, svgEllipse, svgG, svgLine, svgLinearGradient, svgMask, svgPath, svgPattern, svgPolygon, svgPolyline, svgRadialGradient, svgRect, svgRoot, svgStop, svgSymbol, svgText, svgTspan, svgUse, text, } from '@tiendanube/nube-sdk-ui';
import { parseHtml, parseInlineStyle } from './htmlParse.js';
/**
 * The merchant's thank-you HTML as a **component tree**, so the NubeSDK path renders the design
 * instead of a transcript of it.
 *
 * ## Why this exists
 *
 * The host has no raw-HTML component — verified exhaustively against `nube-sdk-types@0.90.0` and
 * `nube-sdk-ui@0.22.1`: no `srcdoc`, no `dangerouslySetInnerHTML`, no passthrough flag, and the JSX
 * runtime throws `Unsupported component` for anything that is not one of the published
 * constructors. `markdown` takes `{ content: string }` and the host strips every tag from it
 * (`thankYou.ts`), which is why a card-based template arrived as a centred run-on of plain lines.
 *
 * So the only way to render the design is to rebuild it out of components. That is exactly what the
 * form itself already does — `buildCodForm` never touches HTML either — and this brings the
 * confirmation page onto the same footing.
 *
 * ## Why it is cheap
 *
 * `NubeComponentStyle` is full csstype `CSS.Properties`, so every inline declaration the templates
 * use passes through **verbatim**: `margin: '0 auto'`, `border: '1px solid #F0E4D8'`,
 * `borderRadius: '50%'`, `background: 'linear-gradient(...)'`, even the `repeating-linear-gradient`
 * barcode strip. There is no shorthand expansion and no CSS engine here — the mapping is tag →
 * constructor, and `style=` → `style`.
 *
 * The five shipped templates use ten tags (`div p span h2 strong br svg path circle rect`) and
 * thirty CSS properties between them, and the SDK has a primitive for every one.
 *
 * ## What it deliberately does not do
 *
 * It is not a browser. Unknown tags render their children and drop the wrapper; anything it cannot
 * read at all yields `null` so the caller falls back to `htmlToMarkdown` — the failure mode is
 * exactly today's behaviour, never worse than it. A merchant who pastes plain text or markdown
 * (rather than HTML) also gets `null`, because the markdown component genuinely renders that better.
 *
 * ⚠️ **Styling channel.** Values land in the `style` prop, which per `CLAUDE.md`'s measured table
 * wins only where the storefront theme has no competing rule. Our nodes are `box`/`txt`, not `p` or
 * `h2`, so a theme's element selectors should not reach them — but if a theme is ever seen fighting
 * a template, the fix is to move the emit channel to `styled()` (which does win), not to add
 * `!important` (which is not a mechanism). `styleOf` is the one place that would change.
 */
/** Tags that become a `text` node. Everything here is prose, not layout. */
const TEXT_TAGS = new Set([
    'p',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'span',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'strike',
    'del',
    'ins',
    'small',
    'mark',
    'label',
    'blockquote',
    'figcaption',
    'li',
    'dt',
    'dd',
    'code',
    'pre',
]);
/** Tags that become a `box`. `tr` is the one that becomes a `row`. */
const BOX_TAGS = new Set([
    'div',
    'section',
    'article',
    'header',
    'footer',
    'main',
    'aside',
    'nav',
    'figure',
    'form',
    'center',
    'ul',
    'ol',
    'dl',
]);
/**
 * Tags that render as nothing: document metadata, and elements whose own children are fallback
 * content the legacy path does not paint either (an `<iframe>`'s text, a `<video>`'s).
 *
 * ⚠️ Nothing here may CARRY text a buyer should read. `button`/`select`/`option`/`textarea` were in
 * this set briefly and do not belong: the legacy bundle renders those controls with their labels
 * visible, so dropping them silently deletes a merchant's words. They fall through to the
 * transparent branch instead, which keeps the text and skips the control — the nearest thing a
 * worker can do.
 *
 * 🚨 A merchant who pastes a whole document (`<!DOCTYPE html><html><head>…`) is the normal case,
 * not the exotic one — every editor's "export HTML" writes one, and the page this was reported on
 * was an email template. An unknown tag is TRANSPARENT below (children render, wrapper drops),
 * which is right for an unrecognised layout wrapper and wrong for `<head>`: the buyer's
 * confirmation opened with the line `Order Confirmation`, which was the `<title>`. The legacy path
 * never showed it, because `innerHTML` hands `<title>` to the UA stylesheet's `display: none`.
 */
const DROPPED_TAGS = new Set([
    'head',
    'title',
    'meta',
    'link',
    'base',
    'noscript',
    'template',
    'colgroup',
    'col',
    'iframe',
    'object',
    'embed',
    'param',
    'source',
    'track',
    'audio',
    'video',
    'canvas',
    'area',
    'input',
]);
/**
 * Wrappers that render their children and nothing of their own.
 *
 * They have no box on the legacy path either: assigning a full document to a `div`'s `innerHTML`
 * runs the fragment parser, which discards `<html>` and `<body>` — tags AND attributes — so a
 * `<body style="background:#f4f5f7">` paints nothing there. Keeping the box here would hand SDK
 * buyers a page-grey band that no Amazonas buyer sees.
 */
const TRANSPARENT_TAGS = new Set(['html', 'body']);
/** Table structure. `tr` becomes a `row`; `td`/`th` become its flex items — see `cellNode`. */
const TABLE_TAGS = new Set(['table', 'thead', 'tbody', 'tfoot']);
const CELL_TAGS = new Set(['td', 'th']);
/** Tags rendered inline, so they flow inside a sentence rather than breaking it. */
const INLINE_TAGS = new Set([
    'span',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'strike',
    'del',
    'ins',
    'small',
    'mark',
    'label',
    'code',
]);
/** Emphasis carried as a text modifier rather than a style declaration. */
const MODIFIERS = {
    strong: 'bold',
    b: 'bold',
    em: 'italic',
    i: 'italic',
    u: 'underline',
    s: 'strike',
    strike: 'strike',
    del: 'strike',
};
/**
 * The SVG primitives, keyed by their markup name.
 *
 * `icons.ts` already ports our own icon set this way by hand; this is the same mapping applied to
 * whatever SVG the merchant's template carries — the heart badge, the wallet glyph, the barcode.
 */
const SVG_TAGS = {
    svg: svgRoot,
    circle: svgCircle,
    clippath: svgClipPath,
    defs: svgDefs,
    ellipse: svgEllipse,
    g: svgG,
    line: svgLine,
    lineargradient: svgLinearGradient,
    mask: svgMask,
    path: svgPath,
    pattern: svgPattern,
    polygon: svgPolygon,
    polyline: svgPolyline,
    radialgradient: svgRadialGradient,
    rect: svgRect,
    stop: svgStop,
    symbol: svgSymbol,
    text: svgText,
    tspan: svgTspan,
    use: svgUse,
};
/**
 * SVG attributes whose SDK prop is camelCase but whose markup name has no hyphen to convert.
 *
 * `parseHtml` lowercases attribute names — correct for HTML, which is case-insensitive — so
 * `viewBox` arrives as `viewbox` and would be silently ignored by the host, collapsing every icon
 * to a 24×24 sliver of its intended geometry. Hyphenated names (`stroke-width`, `stroke-linecap`)
 * need no entry: `camelCaseProperty` already handles them.
 */
const SVG_ATTR_ALIASES = {
    viewbox: 'viewBox',
    preserveaspectratio: 'preserveAspectRatio',
    gradientunits: 'gradientUnits',
    gradienttransform: 'gradientTransform',
    spreadmethod: 'spreadMethod',
    patternunits: 'patternUnits',
    patterncontentunits: 'patternContentUnits',
    patterntransform: 'patternTransform',
    clippathunits: 'clipPathUnits',
    maskunits: 'maskUnits',
    maskcontentunits: 'maskContentUnits',
    startoffset: 'startOffset',
    textlength: 'textLength',
    lengthadjust: 'lengthAdjust',
    stddeviation: 'stdDeviation',
    filterunits: 'filterUnits',
    primitiveunits: 'primitiveUnits',
};
/** Attributes that describe the document rather than the drawing. */
const DROPPED_ATTRS = new Set(['xmlns', 'xmlns:xlink', 'class', 'id', 'style', 'version']);
/** Every tag this converter renders as itself. Used by the templates' vocabulary guard. */
export const SUPPORTED_TAGS = new Set([
    ...TEXT_TAGS,
    ...BOX_TAGS,
    ...TABLE_TAGS,
    ...CELL_TAGS,
    ...TRANSPARENT_TAGS,
    ...Object.keys(SVG_TAGS),
    'tr',
    'a',
    'img',
    'br',
    'hr',
]);
/**
 * The containment every generated container carries.
 *
 * 🚨 **Without it a 600px design does not shrink — it drags the whole confirmation off the panel.**
 * Every `box` is a flex container, so every node here is also a flex ITEM, and a flex item's default
 * `min-width: auto` floors it at its content's min-content width. A percentage `max-width` counts as
 * `none` while that intrinsic size is measured (the containing block is not definite yet), so a
 * merchant's own `width:600px;max-width:100%` contributed a hard 600px through every ancestor.
 * Measured on a live store: the confirmation laid out at 600px inside a 340px panel and, because
 * `successPanel` centres its items, spilled off BOTH edges with only the middle strip readable.
 *
 * `minWidth: '0'` is what lets the chain shrink; `maxWidth: '100%'` is then resolvable and caps each
 * box at its parent; `boxSizing` is the per-object copy of the legacy `.cod-root * { box-sizing:
 * border-box }`, which has no equivalent here because `styled()` rejects `*`. Text wraps rather than
 * spilling out of the narrowed boxes, because `panelBase` sets the inherited `overflow-wrap:
 * anywhere` for the whole tree.
 *
 * Spread FIRST, so a merchant who states any of the three keeps their own value.
 */
const FIT = { minWidth: '0', maxWidth: '100%', boxSizing: 'border-box' };
/** An HTML length attribute (`width="600"`, `width="50%"`) as a CSS length. */
function lengthAttr(value) {
    const raw = value?.trim();
    if (!raw)
        return undefined;
    if (/^\d+(\.\d+)?%$/.test(raw))
        return raw;
    return /^\d+(\.\d+)?$/.test(raw) ? `${raw}px` : undefined;
}
/** `align` on a cell of bare text. */
const ALIGN_TEXT = {
    left: 'left',
    center: 'center',
    right: 'right',
    justify: 'justify',
};
/** `align` on a cell holding elements — the boxes move, not the words inside them. */
const ALIGN_ITEMS = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
};
/** `valign`, which positions the cell itself within its row. */
const VALIGN_SELF = {
    top: 'flex-start',
    middle: 'center',
    bottom: 'flex-end',
};
function styleOf(element) {
    const declarations = parseInlineStyle(element.attrs.style ?? '');
    return Object.keys(declarations).length ? declarations : undefined;
}
/**
 * Which way a container stacks its children.
 *
 * 🚨 **A `box` is a flex container and its `direction` defaults to `row`.** HTML block elements
 * stack, so leaving it unset laid the merchant's whole page out side by side: the header band, the
 * paragraph and every card became columns of a single row, each squeezed narrow enough to wrap one
 * word per line. It reads as a styling bug and is a layout one — the colours all landed.
 *
 * `col` is therefore the default, matching `<div>`. The exception is a container whose own CSS
 * already makes it a flex row, which the templates use 25 times for their label/amount lines
 * (Envío, Total): `display:flex` with no `flex-direction` is a row by CSS's own rules.
 *
 * Read from `direction` rather than passing `flexDirection` through the `style` prop, because it is
 * the SDK's declared API for this and so cannot lose a precedence fight with the host's own default.
 */
function directionOf(style) {
    const declarations = style;
    const flexDirection = declarations?.flexDirection;
    if (typeof flexDirection === 'string')
        return flexDirection.startsWith('column') ? 'col' : 'row';
    const display = declarations?.display;
    return display === 'flex' || display === 'inline-flex' ? 'row' : 'col';
}
/** Markup attributes as SDK props: aliases first, then kebab→camel, dropping document noise. */
function svgProps(element) {
    const props = {};
    for (const [name, value] of Object.entries(element.attrs)) {
        if (DROPPED_ATTRS.has(name))
            continue;
        const alias = SVG_ATTR_ALIASES[name];
        props[alias ?? name.replace(/-([a-z])/g, (_d, c) => c.toUpperCase())] = value;
    }
    const style = styleOf(element);
    if (style)
        props.style = style;
    return props;
}
/**
 * Trim a text node's children the way a browser's whitespace collapsing would.
 *
 * The templates indent their markup, so a `<p>` reads as `"\n      {{greeting}}\n    "`. Runs are
 * already collapsed to single spaces by the caller; this removes what is left hanging at the two
 * ends, which would otherwise render as a leading indent inside every paragraph.
 */
function trimEdges(children) {
    const out = [...children];
    if (typeof out[0] === 'string') {
        const trimmed = out[0].replace(/^\s+/, '');
        if (trimmed)
            out[0] = trimmed;
        else
            out.shift();
    }
    const last = out.length - 1;
    if (last >= 0 && typeof out[last] === 'string') {
        const trimmed = out[last].replace(/\s+$/, '');
        if (trimmed)
            out[last] = trimmed;
        else
            out.pop();
    }
    return out;
}
function convertChildren(nodes, ctx) {
    const out = [];
    for (const node of nodes)
        out.push(...convert(node, ctx));
    return out;
}
/**
 * Children for a container, which can only hold components.
 *
 * Loose text becomes a `text` node so a merchant's stray sentence between two `<div>`s still shows;
 * whitespace-only runs are the markup's own indentation and are dropped.
 */
function blockChildren(nodes, ctx) {
    const out = [];
    for (const child of convertChildren(nodes, ctx)) {
        if (typeof child !== 'string')
            out.push(child);
        else if (child.trim())
            out.push(text({ children: [child] }));
    }
    return out;
}
/**
 * A `<td>` / `<th>`: a flex item inside its `<tr>` row.
 *
 * 🚨 **A cell is a CONTAINER, not text.** Both tags used to sit in `TEXT_TAGS` — defensible for a
 * data table, wrong for the layout tables every email template is built from. The cell holding the
 * merchant's entire card became a `txt`, i.e. a paragraph with block boxes inside it.
 *
 * Sizing approximates the table algorithm closely enough for the two shapes that matter. A cell with
 * no declared width shares its row equally (`flex: 1 1 0%`), which is what turns a two-cell
 * label/amount row into the left/right pair it is meant to be — without it the row packed both cells
 * at the start and a live store rendered `Order total8,48 MXN` as one word. A cell with `width="50%"`
 * takes exactly that instead.
 *
 * `align` follows **Blink rather than the letter of the spec**, because that is what the legacy
 * bundle renders. Chrome maps `align` on a cell to an internal `text-align` value that positions the
 * cell's own boxes but inherits as `start`: measured, `td[align=center]` computes `-webkit-center`
 * while the table nested inside it computes `start`. So a cell of bare text takes `textAlign` and a
 * cell holding elements takes `alignItems`, which is the same distinction expressed in flexbox.
 */
function cellNode(element, ctx) {
    const style = styleOf(element);
    const declared = style?.width ?? lengthAttr(element.attrs.width);
    const colspan = Number.parseInt(element.attrs.colspan ?? '', 10);
    const align = element.attrs.align?.toLowerCase() ?? '';
    const valign = element.attrs.valign?.toLowerCase() ?? '';
    const hasElements = element.children.some((child) => child.kind === 'element');
    const alignment = hasElements ? ALIGN_ITEMS[align] : ALIGN_TEXT[align];
    const children = blockChildren(element.children, ctx);
    if (!children.length && !style)
        return [];
    return [
        box({
            direction: directionOf(style),
            style: {
                ...FIT,
                // A declared width is a basis the cell keeps; anything else shares the row, widened by a
                // `colspan` so a cell spanning two columns is twice its neighbour rather than equal to it.
                ...(declared
                    ? { flexGrow: '0', flexShrink: '1', flexBasis: String(declared) }
                    : {
                        flexGrow: String(Number.isFinite(colspan) && colspan > 1 ? colspan : 1),
                        flexShrink: '1',
                        flexBasis: '0%',
                    }),
                ...(alignment ? (hasElements ? { alignItems: alignment } : { textAlign: alignment }) : {}),
                ...(VALIGN_SELF[valign] ? { alignSelf: VALIGN_SELF[valign] } : {}),
                ...(element.attrs.bgcolor ? { backgroundColor: element.attrs.bgcolor } : {}),
                ...style,
            },
            ...(children.length ? { children } : {}),
        }),
    ];
}
/**
 * A `<table>` — and its `thead`/`tbody`/`tfoot` — as a full-width column of rows.
 *
 * 🚨 **The author's own width is REPLACED by `100%`, deliberately.** Email HTML is built around a
 * 600px table and our panel is 460px at its widest, so honouring the declared width is a card that
 * hangs over the storefront. `max-width: 100%` cannot rescue it either, for the intrinsic-sizing
 * reason recorded on `FIT` — which is exactly what the merchant's `width:600px;max-width:100%`
 * asked for and did not get. The legacy bundle states the same rule as CSS
 * (`.cod-thankyou-page table { width: 100% !important }`), so both bundles reflow one design to one
 * width instead of disagreeing about a page that fits neither.
 */
function tableNode(element, ctx) {
    const style = styleOf(element);
    const children = blockChildren(element.children, ctx);
    if (!children.length && !style)
        return [];
    return [
        box({
            direction: 'col',
            style: { ...FIT, ...style, width: '100%' },
            ...(children.length ? { children } : {}),
        }),
    ];
}
function convert(node, ctx) {
    if (node.kind === 'text') {
        // HTML collapses every run of whitespace to a single space. Done before `<br>` becomes a
        // newline, so the two cannot be confused and real line breaks survive the collapse.
        const collapsed = node.text.replace(/\s+/g, ' ');
        return collapsed ? [collapsed] : [];
    }
    const { tag } = node;
    // Metadata renders nothing; `<html>`/`<body>` render only their children. Both before the SVG
    // branch, because `<title>` is a legal name inside `<svg>` too and must stay `svgText`'s neighbour
    // rather than being dropped there.
    if (!ctx.inSvg) {
        if (DROPPED_TAGS.has(tag))
            return [];
        if (TRANSPARENT_TAGS.has(tag))
            return convertChildren(node.children, ctx);
    }
    if (ctx.inSvg || tag === 'svg') {
        const construct = SVG_TAGS[tag];
        if (!construct)
            return [];
        const children = node.children.length
            ? blockChildren(node.children, { inSvg: true })
            : undefined;
        return [construct({ ...svgProps(node), ...(children?.length ? { children } : {}) })];
    }
    if (tag === 'br')
        return ['\n'];
    if (tag === 'hr') {
        return [
            box({
                style: { ...FIT, borderTop: '1px solid currentColor', opacity: '0.25', width: '100%' },
            }),
        ];
    }
    if (tag === 'img') {
        const src = node.attrs.src;
        if (!src)
            return [];
        // The presentational `width`/`height` first, then the cap, then the merchant's own CSS — the
        // exact precedence the legacy path has, where `.cod-thankyou-page img { max-width: 100%;
        // height: auto }` outranks the attributes and loses to an inline style. Without the cap a
        // 600px banner is the one node that can still widen the panel, since `FIT` does not reach it.
        const size = {
            ...(lengthAttr(node.attrs.width) ? { width: lengthAttr(node.attrs.width) } : {}),
            ...(lengthAttr(node.attrs.height) ? { height: lengthAttr(node.attrs.height) } : {}),
        };
        const style = { ...size, maxWidth: '100%', height: 'auto', ...styleOf(node) };
        return [image({ src, alt: node.attrs.alt ?? '', style })];
    }
    if (tag === 'a') {
        const href = node.attrs.href;
        const children = trimEdges(convertChildren(node.children, ctx));
        if (!children.length)
            return [];
        if (!href)
            return children;
        const style = styleOf(node);
        // `target` is not forwarded: the host shows its own "you are leaving the store" dialog for any
        // cross-origin href regardless (CLAUDE.md), so the only effect would be an inconsistent one.
        return [link({ href, children, ...(style ? { style } : {}) })];
    }
    if (TEXT_TAGS.has(tag)) {
        const children = trimEdges(convertChildren(node.children, ctx));
        if (!children.length)
            return [];
        const style = styleOf(node) ?? {};
        // `<br>` became a newline, which only renders as one if the box is told to keep it.
        const hasBreak = children.some((child) => typeof child === 'string' && child.includes('\n'));
        if (hasBreak)
            style.whiteSpace = 'pre-line';
        const heading = /^h([1-6])$/.exec(tag);
        const modifier = MODIFIERS[tag];
        const inline = INLINE_TAGS.has(tag);
        // Block prose is a flex item like any container and needs the same room to shrink; an inline
        // run is not a box at all, so `maxWidth`/`boxSizing` would be inert on it and are left off.
        const fitted = inline ? style : { ...FIT, ...style };
        return [
            text({
                children: tag === 'li' ? ['• ', ...children] : children,
                ...(Object.keys(fitted).length ? { style: fitted } : {}),
                ...(heading ? { heading: Number(heading[1]) } : {}),
                ...(modifier ? { modifiers: [modifier] } : {}),
                ...(inline ? { inline: true } : {}),
            }),
        ];
    }
    if (TABLE_TAGS.has(tag))
        return tableNode(node, ctx);
    if (CELL_TAGS.has(tag))
        return cellNode(node, ctx);
    if (BOX_TAGS.has(tag) || tag === 'tr') {
        const children = blockChildren(node.children, ctx);
        const style = styleOf(node);
        // An empty styled box is not nothing — the barcode strip in the receipt template is exactly
        // that, a gradient with no content. Drop it only when it carries no styling either.
        if (!children.length && !style)
            return [];
        const props = {
            // A row fills its table: it is the cells inside it that divide the width up.
            style: { ...FIT, ...(tag === 'tr' ? { width: '100%' } : {}), ...style },
            ...(children.length ? { children } : {}),
        };
        return [tag === 'tr' ? row(props) : box({ direction: directionOf(style), ...props })];
    }
    // An unknown tag is transparent: its children still render, its wrapper does not. Better than
    // dropping the subtree, and better than guessing a layout the tag may not have had.
    return convertChildren(node.children, ctx);
}
/**
 * Convert `html` to a component tree, or `null` when the caller should fall back to markdown.
 *
 * `null` means one of two things, both of which markdown handles better than a synthetic tree
 * would: nothing renderable came out, or the input carried no markup at all (a merchant who typed
 * prose, or markdown, into the HTML box).
 */
export function htmlToTree(html) {
    if (!html.trim())
        return null;
    const parsed = parseHtml(html);
    const hasMarkup = parsed.some(function search(node) {
        return node.kind === 'element' && (SUPPORTED_TAGS.has(node.tag) || node.children.some(search));
    });
    if (!hasMarkup)
        return null;
    const children = blockChildren(parsed, { inSvg: false });
    if (!children.length)
        return null;
    // A single root (every shipped template) stays unwrapped, so the tree has no redundant level.
    return children.length === 1 ? (children[0] ?? null) : fragment({ children });
}
//# sourceMappingURL=htmlTree.js.map